/**
 * Reading the account's resolved discount promise — shared by
 * `lib/activation-incentive.ts` (the unused-incentive nudge),
 * `pages/WelcomePage.tsx`, `pages/PricingPage.tsx`, and
 * `components/settings/CRMSubscriptionSection.tsx` so the four surfaces can
 * never disagree.
 *
 * This module used to DERIVE the promise here in TypeScript: D-OFFER-3
 * precedence (per-account offer beats global catalog), the founding-membership
 * fallback, the `foundingDiscountMonthsRemaining > 0` window gate, and the F2
 * remaining-vs-frozen-total rule were all reimplemented client-side from the
 * raw wire fields. The backend resolves the same thing — from the very
 * resolver its Stripe coupon is minted from — so there were two
 * implementations of one set of money rules, and they drifted: a starter
 * credit divided by the discounted rate forever promised "first 3 months on
 * us" on an offer whose invoices paid two.
 *
 * The rules now live in `crmbilling.ResolveActivationPricing` and arrive
 * pre-resolved in the `activation` block. What is left here is reading it and
 * phrasing it.
 */
import type { CRMBillingActivationVM, CRMBillingSummaryVM } from "./view-model";
import { FALLBACK_CATALOG } from "./view-model";

export interface ResolvedFoundingDiscount {
  /** Whether an "N% off for the next M months" promise is honest right now. */
  active: boolean;
  /** Percent off. Meaningful only when `active`. */
  percent: number;
  /** Months REMAINING in the window. Meaningful only when `active`. */
  months: number;
}

type BillingActivationInputs = Pick<
  CRMBillingSummaryVM,
  "activation" | "catalog"
>;

/**
 * NO_PROMISE is the fail-closed degrade for a response with no `activation`
 * block: list price, no discount, no covered months.
 *
 * It deliberately does NOT reconstruct an answer from `catalog` / `offer` /
 * `foundingDiscountMonthsRemaining`. Reconstructing is precisely the habit
 * this refactor removes, and the failure modes are not symmetric — quoting
 * list price to someone who holds a discount is a bad experience, while
 * promising free months the invoice won't honour is a false claim about
 * money. Only one of those is acceptable to guess at.
 *
 * The backend sends `activation` unconditionally (no omitempty), so this is
 * reachable only by a stale client mid-deploy or an un-updated fixture.
 */
function noPromise(
  catalog: CRMBillingSummaryVM["catalog"],
): CRMBillingActivationVM {
  return {
    discountActive: false,
    discountPercent: 0,
    discountMonths: 0,
    effectiveMonthlyCents:
      catalog?.monthlyCents ?? FALLBACK_CATALOG.monthlyCents,
    effectiveAnnualCents: catalog?.annualCents ?? FALLBACK_CATALOG.annualCents,
    // Full annual price due today — no credit applied, because without the
    // resolved block we cannot know one was promised.
    annualDueTodayCents: catalog?.annualCents ?? FALLBACK_CATALOG.annualCents,
    starterCoversMonths: 0,
    starterHasRemainder: false,
  };
}

/**
 * The account's resolved money picture, or the fail-closed `noPromise`
 * degrade. Every pricing/promise surface goes through this rather than
 * touching `billing.activation` directly, so the degrade is applied in one
 * place instead of four.
 */
export function resolveActivation(
  billing: BillingActivationInputs | null | undefined,
): CRMBillingActivationVM {
  return billing?.activation ?? noPromise(billing?.catalog ?? null);
}

/**
 * The discount promise in the shape the phrase renderer wants. A projection
 * of `resolveActivation`, not a second source of truth.
 */
export function resolveFoundingDiscount(
  billing: BillingActivationInputs | null | undefined,
): ResolvedFoundingDiscount {
  const activation = resolveActivation(billing);
  return {
    active: activation.discountActive,
    percent: activation.discountPercent,
    months: activation.discountMonths,
  };
}

/**
 * "N% off for the next M months" — the core discount phrase, brand-prefixed
 * with "Founding member — " ONLY when `foundingMember` is true. A decoupled
 * acquisition-campaign offer (spec D-OFFER-3) can be active for an account
 * that never held founding status; branding that promise "Founding member"
 * would be a false claim.
 *
 * F2: "…for the next M months" (never "…your first M months") because
 * `discount.months` is the REMAINING months in the window — "first" would
 * misrepresent a partially-consumed window (e.g. 5 of 12 remaining read as
 * "your first 12 months", overpromising every month after the first) while
 * "for the next M months" is honest whether the window is fresh or
 * mid-consumption.
 *
 * Copy, not math — which is why it stayed on this side of the boundary while
 * the arithmetic moved to Go.
 */
export function foundingDiscountPhrase(
  discount: Pick<ResolvedFoundingDiscount, "percent" | "months">,
  foundingMember: boolean,
): string {
  const unit = discount.months === 1 ? "month" : "months";
  const core = `${discount.percent}% off for the next ${discount.months} ${unit}`;
  return foundingMember ? `Founding member — ${core}` : core;
}
